import { useState, useEffect, type FC, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminPostsPath } from '../../shared/config/env';
import './NewPassword.css';

/**
 * 新しいパスワード設定画面コンポーネント
 */
const NewPassword: FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { completeNewPassword, requiresNewPassword, pendingUsername } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminPostsPath = getAdminPostsPath();

  // パスワード変更が必要でない場合はログイン画面にリダイレクト
  useEffect(() => {
    if (!requiresNewPassword) {
      navigate(adminLoginPath, { replace: true });
    }
  }, [requiresNewPassword, navigate, adminLoginPath]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // パスワードの検証
    if (newPassword.length < 8) {
      setError('パスワードは8文字以上である必要があります');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    setIsLoading(true);

    try {
      await completeNewPassword(newPassword);
      navigate(adminPostsPath, { replace: true });
    } catch (err: any) {
      if (err.code === 'InvalidPasswordException') {
        setError('パスワードが要件を満たしていません');
      } else if (err.code === 'NotAuthorizedException') {
        setError('認証に失敗しました。再度ログインしてください');
        navigate(adminLoginPath, { replace: true });
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('パスワードの変更に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!requiresNewPassword) {
    return null;
  }

  return (
    <div className="new-password-container">
      <div className="new-password-card">
        <h1 className="new-password-title">新しいパスワードの設定</h1>
        <p className="new-password-description">
          初回ログインのため、新しいパスワードを設定してください。
          {pendingUsername && (
            <span className="new-password-username">ユーザー: {pendingUsername}</span>
          )}
        </p>
        <form onSubmit={handleSubmit} className="new-password-form">
          {error && <div className="new-password-error">{error}</div>}
          <div className="new-password-field">
            <label htmlFor="newPassword">新しいパスワード</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="new-password"
              minLength={8}
              placeholder="8文字以上"
            />
          </div>
          <div className="new-password-field">
            <label htmlFor="confirmPassword">パスワード（確認）</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="new-password"
              minLength={8}
              placeholder="パスワードを再入力"
            />
          </div>
          <button
            type="submit"
            className="new-password-button"
            disabled={isLoading}
          >
            {isLoading ? '設定中...' : 'パスワードを設定'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewPassword;

